import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { SiteContentProvider } from "@/context/SiteContentContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AcceptBooking from "@/pages/AcceptBooking";
import InterpreterDashboard from "@/pages/InterpreterDashboard";
import UserDashboard from "@/pages/UserDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext(null);

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));

  useEffect(() => {
    // Only fetch profile if we have a token but no user data
    // This prevents unnecessary fetches after login (which already provides user data)
    if (token && !user) {
      fetchProfile();
    }
  }, [token]);

  // Use XMLHttpRequest to avoid body stream issues from fetch interceptors
  const apiRequest = (url, options = {}) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(options.method || 'GET', url, true);
      if (options.headers) {
        Object.keys(options.headers).forEach(key => {
          xhr.setRequestHeader(key, options.headers[key]);
        });
      }
      xhr.timeout = 10000;
      xhr.onload = () => {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
        } catch (e) {
          resolve({ ok: false, status: xhr.status, data: null });
        }
      };
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.ontimeout = () => reject(new Error('Request timed out'));
      xhr.send(options.body || null);
    });
  };

  const fetchProfile = async () => {
    try {
      const { ok, status, data } = await apiRequest(`${API}/profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (ok && data) {
        setUser(data);
      } else if (status === 401 || status === 403) {
        console.log("Token invalid or expired, logging out");
        logout();
      } else {
        console.error("Profile fetch failed with status:", status);
        logout();
      }
    } catch (error) {
      console.error("Failed to fetch profile:", error);
      logout();
    }
  };

  const login = (newToken, userData) => {
    // Set user first to prevent unnecessary profile fetch
    setUser(userData);
    localStorage.setItem("token", newToken);
    setToken(newToken);
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const ProtectedRoute = ({ children, allowedRole }) => {
    if (!token) {
      return <Navigate to="/login" replace />;
    }
    if (allowedRole && user && user.role !== allowedRole) {
      return <Navigate to="/" replace />;
    }
    return children;
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, setUser }}>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/interpreter/dashboard"
              element={
                <ProtectedRoute allowedRole="interpreter">
                  <InterpreterDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/user/dashboard"
              element={
                <ProtectedRoute allowedRole="user">
                  <UserDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute allowedRole="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route path="/booking/accept" element={<AcceptBooking />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </AuthContext.Provider>
  );
}

// Wrap App with SiteContentProvider
const AppWithContent = () => (
  <SiteContentProvider>
    <App />
  </SiteContentProvider>
);

export default AppWithContent;